import Typography from '@mui/material/Typography'
import { MainService, RobotRead } from 'api/schema'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import Loader from 'components/Loader/Loader'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PROGRESS_TYPES } from 'utils/constants'
import type { VendorCode } from 'utils/vendorDisplayName'

import RobotsList from './RobotsList/RobotsList'

import style from './SelectRobotModal.module.css'

interface IDialogProps {
  selectedRobotId?: number
  isOpen: boolean
  robotType: string | undefined
  /** When set (e.g. AUTONOMICS), only robots of this vendor are requested and shown. Omit to show all vendors. */
  includeVendor?: VendorCode
  showOnline?: boolean | undefined
  onConfirmClick: (robot: RobotRead | undefined) => void
  onClose: () => void
  robotIdsFilter?: number[]
}

const SelectRobotModal = ({
  isOpen,
  robotType,
  includeVendor,
  onConfirmClick,
  showOnline,
  onClose,
  selectedRobotId,
  robotIdsFilter,
}: React.PropsWithChildren<IDialogProps>) => {
  const { t } = useTranslation()
  const [loadingProgress, setLoadingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [robots, setRobots] = useState<RobotRead[]>([])
  const [selectedRobot, setSelectedRobot] = useState<RobotRead | undefined>(undefined)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const PAGE_SIZE = 20

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const fetchData = async (pageNum: number, search: string, isAppend = false) => {
    try {
      if (!isAppend) {
        setLoadingProgress(PROGRESS_TYPES.WORK)
      } else {
        setIsFetchingMore(true)
      }

      const response = await MainService.mainRobotList(
        ['-id'],
        pageNum,
        PAGE_SIZE,
        search || undefined,
        robotIdsFilter,
        includeVendor || undefined
      )

      const results = Array.isArray(response.results) ? response.results : []
      const filteredResults = results.filter(robot => {
        if (includeVendor && robot.vendor !== includeVendor) return false
        const effectiveType = robot.robot_type
        if (robotType && effectiveType !== robotType) return false
        if (showOnline && robot.network_status !== 'online') return false
        if (robotIdsFilter && !robotIdsFilter.includes(robot.id)) return false
        return true
      })

      if (isAppend) {
        setRobots(prev => [...prev, ...filteredResults])
      } else {
        setRobots(filteredResults)
      }

      setHasMore(pageNum * PAGE_SIZE < (response.total || 0))
      setPage(pageNum)

      if (!isAppend) {
        setLoadingProgress(PROGRESS_TYPES.SUCCESS)
      }
    } catch (error) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        if (!isAppend) {
          setLoadingProgress(PROGRESS_TYPES.IDLE)
        }
        return
      }
      if (!isAppend) {
        setLoadingProgress(PROGRESS_TYPES.ERROR)
      }
      console.warn('[SelectRobotModal] fetchData error:', error)
    } finally {
      setIsFetchingMore(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      fetchData(1, debouncedSearchQuery, false)
    } else {
      setSelectedRobot(undefined)
      setSearchQuery('')
      setDebouncedSearchQuery('')
      setRobots([])
      setPage(1)
      setHasMore(true)
      setLoadingProgress(PROGRESS_TYPES.IDLE)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, debouncedSearchQuery])

  const loadMore = () => {
    if (loadingProgress === PROGRESS_TYPES.SUCCESS && hasMore && !isFetchingMore) {
      fetchData(page + 1, debouncedSearchQuery, true)
    }
  }

  return (
    <ConfirmDialog
      isOpen={isOpen}
      className={style.root}
      onClose={onClose}
      onConfirmClick={() => onConfirmClick(selectedRobot)}
      title={'Select a Robot'}
      confirmText={'Select'}
      confirmButtonDisabled={!selectedRobot}
    >
      <div className={style.content}>
        <input
          className={style.searchInput}
          type="text"
          placeholder={t('common.search') + '...'}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        {loadingProgress === PROGRESS_TYPES.WORK && <Loader />}
        {loadingProgress === PROGRESS_TYPES.SUCCESS && robots.length > 0 && (
          <RobotsList
            robots={robots}
            onSelect={robot => setSelectedRobot(robot)}
            selectedRobot={selectedRobot}
            onScrollBottom={loadMore}
          />
        )}
        {loadingProgress === PROGRESS_TYPES.SUCCESS && robots.length === 0 && (
          <Typography variant="body1" align="center">
            {t('robot.modal_robots_empty')}
          </Typography>
        )}
        {isFetchingMore && (
          <div style={{ padding: '10px', textAlign: 'center' }}>
            <Loader />
          </div>
        )}
      </div>
    </ConfirmDialog>
  )
}

export default SelectRobotModal
